import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button, CardHeader, DatePicker, EmptyState, ErrorNotice, Field, LoadingState, Panel, TextArea, TextInput } from '@bcecloud/portal-ui';
import { Checkbox } from '@/components/ui/checkbox';
import { errorMessage } from '@/lib/api';
import { addEntry, ENTRY_KIND, listEntries, removeEntry, updateEntry, type InquiryEntry, type InquirySubject } from '@/lib/inquiries';

/**
 * BCE's work on one inquiry: the tasks to get it to a proposal, the
 * decisions taken and why, and notes from the client. Three sections, one
 * record each, internal only.
 */

function shortDate(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function stamp(entry: InquiryEntry): string {
  return `${entry.authorName} · ${shortDate(entry.createdAt)}`;
}

export function InquiryWorkroom({ organizationId, subject }: { readonly organizationId: string; readonly subject: InquirySubject }): React.ReactElement {
  const queryClient = useQueryClient();
  const key = ['inquiry-entries', subject.type, subject.id];
  const entries = useQuery({ queryKey: key, queryFn: ({ signal }) => listEntries(organizationId, subject, signal) });
  const refresh = (): Promise<void> => queryClient.invalidateQueries({ queryKey: key });
  const fail = (error: unknown): void => {
    toast.error(errorMessage(error));
  };

  const [task, setTask] = useState('');
  const [taskDue, setTaskDue] = useState('');
  const [decision, setDecision] = useState('');
  const [why, setWhy] = useState('');
  const [note, setNote] = useState('');

  const add = useMutation({
    mutationFn: (input: Parameters<typeof addEntry>[2]) => addEntry(organizationId, subject, input),
    onSuccess: refresh,
    onError: fail,
  });
  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Parameters<typeof updateEntry>[2] }) => updateEntry(organizationId, id, patch),
    onSuccess: refresh,
    onError: fail,
  });
  const remove = useMutation({ mutationFn: (id: string) => removeEntry(organizationId, id), onSuccess: refresh, onError: fail });

  if (entries.isPending) return <LoadingState label="Loading the workroom…" />;
  if (entries.isError) return <ErrorNotice title="Workroom unavailable" message={errorMessage(entries.error)} />;

  const tasks = entries.data.filter((entry) => entry.kind === ENTRY_KIND.task);
  const decisions = entries.data.filter((entry) => entry.kind === ENTRY_KIND.decision);
  const notes = entries.data.filter((entry) => entry.kind === ENTRY_KIND.note);
  const openTasks = tasks.filter((entry) => !entry.done).length;
  const busy = add.isPending || update.isPending || remove.isPending;

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Panel className="p-5 lg:col-span-2">
        <CardHeader
          title="Tasks"
          description={openTasks > 0 ? `${openTasks} still to do before this can go to proposal.` : 'What has to happen before this can go to proposal.'}
        />
        {tasks.length === 0 ? (
          <EmptyState title="No tasks yet" message="Add what has to be done: check a facilitator's dates, price the freight, call the venue." />
        ) : (
          <ul className="divide-y divide-portal-line border-t border-portal-line">
            {tasks.map((entry) => (
              <li key={entry.id} className="flex flex-wrap items-center gap-3 py-2.5">
                <Checkbox checked={entry.done} disabled={busy} onCheckedChange={(state) => update.mutate({ id: entry.id, patch: { done: state === true } })} />
                <span className={`min-w-0 flex-1 text-portal-small ${entry.done ? 'text-portal-muted line-through' : ''}`}>
                  {entry.body}
                  <span className="block text-portal-micro text-portal-muted no-underline">
                    {[entry.dueAt ? `Due ${shortDate(entry.dueAt)}` : null, entry.done && entry.doneAt ? `Done ${shortDate(entry.doneAt)}` : null, stamp(entry)]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </span>
                <Button size="compact" variant="ghost" onClick={() => remove.mutate(entry.id)} disabled={busy}>
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        )}
        <form
          className="mt-4 grid gap-3 sm:grid-cols-[1fr_auto_auto] sm:items-end"
          onSubmit={(event) => {
            event.preventDefault();
            if (!task.trim()) return;
            add.mutate({ kind: ENTRY_KIND.task, body: task.trim(), dueAt: taskDue ? new Date(taskDue).toISOString() : null });
            setTask('');
            setTaskDue('');
          }}
        >
          <Field label="Add a task" htmlFor="inquiry-task">
            <TextInput id="inquiry-task" value={task} onChange={(event) => setTask(event.target.value)} placeholder="What has to happen" />
          </Field>
          <Field label="Due" htmlFor="inquiry-task-due">
            <DatePicker id="inquiry-task-due" value={taskDue} onChange={setTaskDue} placeholder="Optional" />
          </Field>
          <Button type="submit" disabled={busy || task.trim().length === 0}>
            Add task
          </Button>
        </form>
      </Panel>

      <Panel className="p-5">
        <CardHeader title="Decisions" description="What was decided, and why, so nobody has to remember." />
        {decisions.length === 0 ? (
          <EmptyState title="No decisions yet" message="Log each call as it is made: the date to offer, the program to steer toward, the supplement to quote." />
        ) : (
          <ul className="divide-y divide-portal-line border-t border-portal-line">
            {[...decisions].reverse().map((entry) => (
              <li key={entry.id} className="py-3">
                <p className="text-portal-small font-bold">{entry.body}</p>
                {entry.why ? <p className="mt-1 text-portal-small whitespace-pre-line">{entry.why}</p> : null}
                <p className="mt-1 text-portal-micro text-portal-muted">{stamp(entry)}</p>
              </li>
            ))}
          </ul>
        )}
        <form
          className="mt-4 grid gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (!decision.trim()) return;
            add.mutate({ kind: ENTRY_KIND.decision, body: decision.trim(), ...(why.trim() ? { why: why.trim() } : {}) });
            setDecision('');
            setWhy('');
          }}
        >
          <Field label="Decision" htmlFor="inquiry-decision">
            <TextInput id="inquiry-decision" value={decision} onChange={(event) => setDecision(event.target.value)} placeholder="What was decided" />
          </Field>
          <Field label="Why" htmlFor="inquiry-decision-why" hint="The reasoning, in a sentence or two.">
            <TextArea id="inquiry-decision-why" rows={2} value={why} onChange={(event) => setWhy(event.target.value)} />
          </Field>
          <div>
            <Button type="submit" disabled={busy || decision.trim().length === 0}>
              Log decision
            </Button>
          </div>
        </form>
      </Panel>

      <Panel className="p-5">
        <CardHeader title="Notes" description="What the client said on calls and in email, and anything else worth keeping." />
        {notes.length === 0 ? (
          <EmptyState title="No notes yet" message="Log what the client told you after each conversation." />
        ) : (
          <ul className="divide-y divide-portal-line border-t border-portal-line">
            {[...notes].reverse().map((entry) => (
              <li key={entry.id} className="py-3">
                <p className="text-portal-small whitespace-pre-line">{entry.body}</p>
                <p className="mt-1 text-portal-micro text-portal-muted">{stamp(entry)}</p>
              </li>
            ))}
          </ul>
        )}
        <form
          className="mt-4 grid gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (!note.trim()) return;
            add.mutate({ kind: ENTRY_KIND.note, body: note.trim() });
            setNote('');
          }}
        >
          <Field label="Add a note" htmlFor="inquiry-note">
            <TextArea id="inquiry-note" rows={3} value={note} onChange={(event) => setNote(event.target.value)} placeholder="What the client said, and when" />
          </Field>
          <div>
            <Button type="submit" disabled={busy || note.trim().length === 0}>
              Add note
            </Button>
          </div>
        </form>
      </Panel>
    </div>
  );
}
